import QRCode from 'qrcode';

/**
 * Printing utilities for drone labels
 * Supports two label formats:
 * 1. Standard labels: 1.5" x 0.375" (text with optional FAA registration)
 * 2. Square QR labels: 11.4mm x 11.4mm (QR left, human-readable serial right)
 */

// ============================================================================
// STANDARD LABELS (1.5" x 0.375") - Text only
// ============================================================================

/**
 * Shared by printStandardLabels and printFreeTextLabels so a free-typed label
 * and a serial label come off the roll identically.
 *
 * NOTE: printSingleStandardLabel below carries its own near-copy with an extra
 * `margin-top: 3px` on .container. That difference predates this constant and
 * is left alone deliberately — folding it in here would silently shift every
 * single-drone label 3px.
 */
const STANDARD_LABEL_STYLES = `
        .label-page {
            width: 1.5in;
            height: 0.375in;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
        }
        .label {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-size: 12px;
            font-family: 'Roboto Mono', monospace;
        }
        .label-row {
            width: 100%;
            text-align: center;
            padding: 0;
            font-weight: bold;
        }
    `;

/**
 * Print standard 1.5" x 0.375" text labels for multiple serials
 * @param {Array} serials - Array of {serialnumber, ...} objects
 * @param {Object} options - { includeFAA: boolean, getFAARegistration: fn }
 */
export async function printStandardLabels(serials, options = {}) {
    const { includeFAA = false, getFAARegistration = () => null } = options;

    if (!serials || serials.length === 0) {
        return; // Caller should validate before calling
    }

    const labelsHTML = serials.map(serial => {
        const faaReg = includeFAA ? getFAARegistration(serial.serialnumber) : null;
        return `
            <div class="label-page">
                <div class="container">
                    <div class="label">
                        <div class="label-row">${serial.serialnumber || 'N/A'}</div>
                        ${faaReg ? `<div class="label-row">${faaReg}</div>` : ''}
                    </div>
                </div>
            </div>
        `;
    }).join('');

    openPrintWindow(labelsHTML, STANDARD_LABEL_STYLES, `Labels (${serials.length})`, '1.5in 0.375in');
}

/**
 * Print a single standard label for one drone
 */
export function printSingleStandardLabel(drone) {
    const faaReg = drone.faa_reg;
    const labelsHTML = `
        <div class="label-page">
            <div class="container">
                <div class="label">
                    <div class="label-row">${drone.serialnumber || 'N/A'}</div>
                    ${faaReg ? `<div class="label-row">${faaReg}</div>` : ''}
                </div>
            </div>
        </div>
    `;

    const styles = `
        .label-page {
            width: 1.5in;
            height: 0.375in;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            margin-top: 3px;
        }
        .label {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-size: 12px;
            font-family: 'Roboto Mono', monospace;
        }
        .label-row {
            width: 100%;
            text-align: center;
            padding: 0;
            font-weight: bold;
        }
    `;

    openPrintWindow(labelsHTML, styles, 'Print Label', '1.5in 0.375in');
}

// ============================================================================
// SQUARE QR LABELS (11.4mm x 11.4mm, two per row)
// ============================================================================

/**
 * The roll geometry every 11.4mm label shares: a 28.5mm x 11.4mm page holding
 * two 11.4mm tile positions with a 3mm die-cut gap. Drone stickers, box
 * instance stickers and free labels all print on this stock, so the dimensions
 * live in ONE place — a copy per feature is how they drift.
 */
const QR_ROW_STYLES = `
        .qr-label-page {
            width: 28.5mm;
            height: 11.4mm;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3mm;
            box-sizing: border-box;
        }
        .qr-square {
            width: 11.4mm;
            height: 11.4mm;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        .qr-img {
            width: 11.4mm;
            height: 11.4mm;
            display: block;
        }
    `;

/**
 * Fine die-cut alignment, applied ON TOP of QR_ROW_STYLES for layouts that put
 * a real tile in BOTH positions. relative + left shifts the painted tile right
 * without disturbing the flex centering/flow. Per-tile absolute shifts, tuned
 * against physical prints: left tile 0.6mm, right tile 1.25mm.
 *
 * NOTE: at 1.25mm the right tile's right edge is ~28.4mm — only ~0.1mm shy of
 * the 28.5mm page edge, so there is no more room to push it right without
 * widening @page or shrinking the QR. ~0.088mm per printer dot at 288 DPI.
 *
 * Deliberately NOT applied to the QR-plus-text layout, whose un-nudged
 * placement is the one proven on drone stickers.
 */
const QR_PAIR_OFFSETS = `
        .qr-square {
            position: relative;
            left: 0.6mm;
        }
        .qr-square:nth-child(2) {
            left: 1.25mm;
        }
    `;

/**
 * Format serial number for human-readable display in 4 rows
 * Input: "F021-0000-0000-0000"
 * Output: ["F021", "0000", "0000", "0000"]
 */
function formatSerialForDisplay(serialnumber) {
    if (!serialnumber) return ['N/A', '', '', ''];
    // Split by dashes and return parts
    const parts = serialnumber.split('-');
    // Ensure we have exactly 4 parts
    while (parts.length < 4) parts.push('');
    return parts.slice(0, 4);
}

/**
 * Print 11.4mm square QR labels for multiple serials
 * Layout: QR code on left square, human-readable serial on right square
 * @param {Array} serials - Array of {serialnumber, ...} objects
 */
export async function printSquareQRLabels(serials) {
    if (!serials || serials.length === 0) {
        return; // Caller should validate before calling
    }

    // Sort alphanumerically
    const sortedSerials = [...serials].sort((a, b) =>
        (a.serialnumber || '').localeCompare(b.serialnumber || '')
    );

    // Generate QR codes - encode full serial WITH dashes
    const qrDataUrls = await Promise.all(
        sortedSerials.map(serial =>
            QRCode.toDataURL(serial.serialnumber || '', {
                width: 130, // ~11.4mm at 288 DPI
                margin: 0,
                errorCorrectionLevel: 'M'
            })
        )
    );

    const labelsHTML = sortedSerials.map((serial, index) => {
        const parts = formatSerialForDisplay(serial.serialnumber);
        return `
            <div class="qr-label-page">
                <div class="qr-square">
                    <img src="${qrDataUrls[index]}" class="qr-img" alt="QR" />
                </div>
                <div class="serial-square">
                    <div class="serial-line">${parts[0]}</div>
                    <div class="serial-line">${parts[1]}</div>
                    <div class="serial-line">${parts[2]}</div>
                    <div class="serial-line">${parts[3]}</div>
                </div>
            </div>
        `;
    }).join('');

    const styles = `
        ${QR_ROW_STYLES}
        .serial-square {
            width: 11.4mm;
            height: 11.4mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-family: 'Roboto Mono', monospace;
            font-size: 7px;
            font-weight: bold;
            line-height: 1.3;
            flex-shrink: 0;
        }
        .serial-line {
            text-align: center;
        }
    `;

    openPrintWindow(labelsHTML, styles, `QR Labels (${sortedSerials.length})`, '28.5mm 11.4mm');
}

/**
 * Print a single 11.4mm square QR label for one drone
 */
export async function printSingleSquareQRLabel(drone) {
    await printSquareQRLabels([{ serialnumber: drone.serialnumber }]);
}

// ============================================================================
// BOX INSTANCE QR LABELS (11.4mm squares, two QR per row, START/END bookends)
// ============================================================================

/**
 * Print 11.4mm QR labels for a receipt box's per-instance items on the SAME
 * roll stock as the drone stickers (28.5mm x 11.4mm rows, two label positions
 * each). Shares every dimension with printSquareQRLabels so the two features
 * can never drift apart again.
 *
 * Unlike drone stickers (QR left + human-readable serial right, one per row),
 * box items pack TWO scannable QR codes per row to double throughput. The run
 * is bookended by START/END marker tiles so a torn-off strip is self-delimiting
 * — you can see where one box's items begin and end:
 *
 *   [START]  [QR 1]
 *   [QR 2]   [QR 3]
 *   [QR 4]   [END]
 *
 * Each QR encodes the part_instances UUID with dashes stripped.
 *
 * @param {Array} instances - [{ id, serial }] rows for one receipt box
 * @param {Object} box - { sku, barcode } shown on the START/END markers
 */
export async function printBoxInstanceQRLabels(instances, box = {}) {
    if (!instances || instances.length === 0) {
        return; // Caller should validate before calling
    }
    const { sku = '', barcode = '' } = box;

    // Stable order by serial; START/END are pinned outside the sort.
    const sorted = [...instances].sort((a, b) =>
        (a.serial || '').localeCompare(b.serial || '')
    );

    // Encode the UUID without dashes — shorter payload keeps the QR scannable
    // at 11.4mm. Higher source resolution than the drone 130px since UUIDs are
    // denser; the 1-module quiet zone plus the die-cut gap give scanners room.
    const qrDataUrls = await Promise.all(
        sorted.map(inst =>
            QRCode.toDataURL(String(inst.id || '').replace(/-/g, ''), {
                width: 200,
                margin: 1,
                errorCorrectionLevel: 'M'
            })
        )
    );

    const markerTile = (word) => `
        <div class="qr-square marker">
            <div class="marker-word">${word}</div>
            <div class="marker-line">${escapeHtml(sku || '—')}</div>
            <div class="marker-line">${escapeHtml(barcode || '—')}</div>
        </div>
    `;
    const qrTile = (dataUrl) => `
        <div class="qr-square">
            <img src="${dataUrl}" class="qr-img" alt="QR" />
        </div>
    `;
    const blankTile = '<div class="qr-square"></div>';

    // START, one tile per instance, END — then pad to an even count so the last
    // row keeps its real tiles in the fixed left/right die-cut positions.
    const tiles = [markerTile('START'), ...qrDataUrls.map(qrTile), markerTile('END')];
    if (tiles.length % 2 === 1) tiles.push(blankTile);

    let rowsHTML = '';
    for (let i = 0; i < tiles.length; i += 2) {
        rowsHTML += `<div class="qr-label-page">${tiles[i]}${tiles[i + 1]}</div>`;
    }

    const styles = `
        ${QR_ROW_STYLES}
        ${QR_PAIR_OFFSETS}
        .marker {
            flex-direction: column;
            justify-content: center;
            font-family: 'Roboto Mono', monospace;
            border: 0.3mm solid #000;
            box-sizing: border-box;
            overflow: hidden;
            padding: 0.3mm;
        }
        .marker-word {
            font-size: 6px;
            font-weight: 800;
            letter-spacing: 0.5px;
        }
        .marker-line {
            width: 100%;
            font-size: 5px;
            font-weight: bold;
            line-height: 1.15;
            text-align: center;
            word-break: break-all;
        }
    `;

    openPrintWindow(rowsHTML, styles, `Box QR Labels (${sorted.length})`, '28.5mm 11.4mm');
}

// ============================================================================
// FREE LABELS (type anything, print N of it)
// ============================================================================

/**
 * The most one run may print. Matches the preprint ceiling on Mint Drone so the
 * shop meets one number, not two.
 */
export const MAX_FREE_LABELS = 1000;

/**
 * Characters that fit one line of the 1.5" wide label.
 *
 * Roboto Mono's advance width is 0.6em, so at 12px each character is 7.2px and
 * the 1.5in (144px) label holds 19.99 of them. Twenty would touch both die-cut
 * edges with zero margin. Nineteen is also what already prints: a drone serial
 * ("F021-0000-0000-0000") is exactly 19 characters and clears with ~7px spare.
 */
export const FREE_TEXT_MAX_CHARS = 19;

/**
 * The 11.4mm (43.1px) text square at 7px Roboto Mono holds 10.26 characters per
 * line and 4 lines at line-height 1.3 (36.4px of 43.1px). Capped one character
 * short of the arithmetic: .caption-square clips rather than bleeds, and a
 * clipped sticker is a reprint.
 */
export const QR_CAPTION_MAX_CHARS = 9;
export const QR_CAPTION_MAX_LINES = 4;

/**
 * Serials never contained markup, so the printers above interpolate them raw.
 * Free text can contain anything, and an unescaped `<` does not fail loudly —
 * the browser swallows it as an unknown tag and the sticker prints SHORT, with
 * nothing on screen to say so. Escape before it reaches document.write.
 */
const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[ch]));

const clampCount = (quantity) =>
    Math.max(1, Math.min(MAX_FREE_LABELS, Math.floor(Number(quantity) || 1)));

/**
 * Greedy word-wrap into the 11.4mm text square. A word too long for one line is
 * hard-broken rather than allowed to overflow the die-cut.
 *
 * Exported so the on-screen preview wraps through the SAME function the printer
 * uses — a preview that disagrees with the print is worse than no preview.
 *
 * @returns {{lines: string[], overflow: boolean}} overflow = text was cut
 */
export function wrapForSquare(text, maxChars = QR_CAPTION_MAX_CHARS, maxLines = QR_CAPTION_MAX_LINES) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let current = '';

    for (const word of words) {
        let rest = word;
        while (rest.length > maxChars) {
            if (current) { lines.push(current); current = ''; }
            lines.push(rest.slice(0, maxChars));
            rest = rest.slice(maxChars);
        }
        if (!rest) continue;
        if (!current) current = rest;
        else if (current.length + 1 + rest.length <= maxChars) current += ` ${rest}`;
        else { lines.push(current); current = rest; }
    }
    if (current) lines.push(current);

    return { lines: lines.slice(0, maxLines), overflow: lines.length > maxLines };
}

/**
 * Print N copies of a free-typed 1.5" x 0.375" text label.
 *
 * Same stock and same styles as the drone serial labels — this only swaps where
 * the two rows come from. Both rows are optional; one empty row centers the
 * other, exactly as a drone label with no FAA registration does.
 *
 * @param {Object} args - { line1, line2, quantity }
 */
export function printFreeTextLabels({ line1 = '', line2 = '', quantity = 1 } = {}) {
    const top = String(line1).trim();
    const bottom = String(line2).trim();
    if (!top && !bottom) {
        return; // Caller should validate before calling
    }
    const count = clampCount(quantity);

    const oneLabel = `
            <div class="label-page">
                <div class="container">
                    <div class="label">
                        ${top ? `<div class="label-row">${escapeHtml(top)}</div>` : ''}
                        ${bottom ? `<div class="label-row">${escapeHtml(bottom)}</div>` : ''}
                    </div>
                </div>
            </div>
        `;

    openPrintWindow(oneLabel.repeat(count), STANDARD_LABEL_STYLES, `Labels (${count})`, '1.5in 0.375in');
}

/**
 * Print a run of DIFFERENT two-row text labels in ONE print window.
 *
 * printFreeTextLabels repeats one label N times; printStandardLabels prints many
 * different ones but composes its rows from a drone serial and an FAA number.
 * Neither takes a list of arbitrary row pairs, which is what a return manifest
 * is: forty boards, forty different identities, forty different reasons, one trip
 * to the printer.
 *
 * Same stock, same STANDARD_LABEL_STYLES and same page size as every other
 * 1.5in x 0.375in label, so a return sticker comes off the roll identical to a
 * drone sticker and nothing needs reloading. This function owns no format.
 *
 * Rows are TRUNCATED at FREE_TEXT_MAX_CHARS rather than refused. The caller is a
 * print button on a list somebody has already built, and refusing the whole run
 * because one reason is a character too long strands forty boards. The callers
 * that can validate earlier (the label maker's counter, the reason vocabulary's
 * VARCHAR(19)) already do.
 *
 * @param {Array} labels   - [{ line1, line2 }], one entry per label
 * @param {Object} options - { copies } copies of EACH label, default 1
 */
export function printReturnLineLabels(labels = [], { copies = 1 } = {}) {
    const rows = (Array.isArray(labels) ? labels : [])
        .map(({ line1 = '', line2 = '' }) => ({
            top: String(line1).trim().slice(0, FREE_TEXT_MAX_CHARS),
            bottom: String(line2).trim().slice(0, FREE_TEXT_MAX_CHARS),
        }))
        .filter(({ top, bottom }) => top || bottom);

    if (rows.length === 0) {
        return; // Caller should validate before calling
    }

    const perLabel = clampCount(copies);

    const body = rows
        .map(({ top, bottom }) => `
            <div class="label-page">
                <div class="container">
                    <div class="label">
                        ${top ? `<div class="label-row">${escapeHtml(top)}</div>` : ''}
                        ${bottom ? `<div class="label-row">${escapeHtml(bottom)}</div>` : ''}
                    </div>
                </div>
            </div>
        `.repeat(perLabel))
        .join('');

    const total = rows.length * perLabel;
    openPrintWindow(body, STANDARD_LABEL_STYLES, `Return labels (${total})`, '1.5in 0.375in');
}

/**
 * Print N copies of a free-typed QR label on the 11.4mm roll stock.
 *
 * Two layouts, both reusing geometry already proven on this stock:
 *   'text' — QR left, the same text wrapped into the right square. One label
 *            per row. Geometry of printSquareQRLabels (no die-cut nudge).
 *   'pair' — two QR codes per row, no text. Geometry of printBoxInstanceQRLabels
 *            (nudged), minus the START/END bookends, which mark the bounds of
 *            ONE box's run and mean nothing for a free label.
 *
 * Every code in the run is identical, so the QR is encoded ONCE and the data
 * URL repeated. The per-serial printers cannot do that and pay one encode per
 * label; a 1000-label run here costs one.
 *
 * @param {Object} args - { text, quantity, mode: 'text'|'pair' }
 */
export async function printFreeQRLabels({ text = '', quantity = 1, mode = 'text' } = {}) {
    const payload = String(text).trim();
    if (!payload) {
        return; // Caller should validate before calling
    }
    const count = clampCount(quantity);

    // width 200 / margin 1 rather than the drone stickers' 130 / 0: a drone
    // serial is a fixed 19 characters that margin 0 was tuned for, while free
    // text can be a long SKU or a URL. The denser the payload, the more the
    // scanner needs the quiet zone and the source resolution.
    const dataUrl = await QRCode.toDataURL(payload, {
        width: 200,
        margin: 1,
        errorCorrectionLevel: 'M',
    });

    const qrTile = `
            <div class="qr-square">
                <img src="${dataUrl}" class="qr-img" alt="QR" />
            </div>
        `;

    let rowsHTML = '';
    let styles = '';

    if (mode === 'pair') {
        // Two per row; pad an odd run so the last tile keeps its left die-cut
        // position instead of being centered across both.
        const blankTile = '<div class="qr-square"></div>';
        for (let i = 0; i < count; i += 2) {
            const right = i + 1 < count ? qrTile : blankTile;
            rowsHTML += `<div class="qr-label-page">${qrTile}${right}</div>`;
        }
        styles = `${QR_ROW_STYLES}${QR_PAIR_OFFSETS}`;
    } else {
        const { lines } = wrapForSquare(payload);
        const captionHTML = lines
            .map((line) => `<div class="caption-line">${escapeHtml(line)}</div>`)
            .join('');
        const oneRow = `
            <div class="qr-label-page">
                ${qrTile}
                <div class="caption-square">${captionHTML}</div>
            </div>
        `;
        rowsHTML = oneRow.repeat(count);
        styles = `
        ${QR_ROW_STYLES}
        .caption-square {
            width: 11.4mm;
            height: 11.4mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-family: 'Roboto Mono', monospace;
            font-size: 7px;
            font-weight: bold;
            line-height: 1.3;
            flex-shrink: 0;
            overflow: hidden;
        }
        .caption-line {
            text-align: center;
        }
    `;
    }

    openPrintWindow(rowsHTML, styles, `QR Labels (${count})`, '28.5mm 11.4mm');
}

// ============================================================================
// COMBINED PRINTING
// ============================================================================

/**
 * Print both label types (opens two windows)
 * @param {Array} serials - Array of {serialnumber, ...} objects
 * @param {Object} options - Options for standard labels { includeFAA, getFAARegistration }
 */
export async function printBothLabelTypes(serials, options = {}) {
    if (!serials || serials.length === 0) {
        return; // Caller should validate before calling
    }

    // Print standard labels first
    await printStandardLabels(serials, options);
    // Then print square QR labels
    await printSquareQRLabels(serials);
}

/**
 * Print both label types for a single drone
 */
export async function printSingleBothLabels(drone) {
    printSingleStandardLabel(drone);
    await printSingleSquareQRLabel(drone);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Open a print window with the given HTML content
 */
function openPrintWindow(bodyContent, styles, title, pageSize) {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        console.warn('Pop-up blocked. Please allow pop-ups to print labels.');
        return;
    }

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${title}</title>
            <link href="https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap" rel="stylesheet">
            <style>
                html, body {
                    height: 100%;
                    margin: 0;
                }
                @media print {
                    @page {
                        margin: 0;
                        size: ${pageSize};
                    }
                    body { margin: 0; }
                    .label-page, .qr-label-page {
                        page-break-after: always;
                        page-break-inside: avoid;
                    }
                    .label-page:last-child, .qr-label-page:last-child {
                        page-break-after: auto;
                    }
                }
                ${styles}
            </style>
        </head>
        <body>
            ${bodyContent}
            <script>
                window.onload = function() {
                    window.print();
                    window.onafterprint = function() { window.close(); };
                }
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
}
